/**
 * A trading plan as lines on the chart — the `derived` half of `setAnnotations`.
 *
 * `plan-levels.ts` decides *what* a plan draws (entry, stop, targets, and the
 * fills as marks); this turns that into the contract's `Annotation` shape so the
 * chart renderer can draw plan levels and user drawings through one path.
 *
 * ## These are computed every render and never saved
 *
 * The renderer takes user annotations and derived ones separately, and only the
 * first round-trips. That separation is the whole design and it is what this
 * file must not break: nothing here may ever be written into `Pane.annotations`.
 * Move the stop and the plan is the single place it changed; mint an `hline`
 * instead and the stored copy renders on, correctly drawn and wrong.
 *
 * `chart-engine`'s `persistable()` is the guard on the way back out, and the
 * `editable: false` these carry (via `origin: "derived"`) is why no grip is
 * offered — a dragged plan line would edit something whose source is elsewhere
 * and the next render would silently undo it.
 *
 * ## Ids are synthetic, and that is safe precisely because these never persist
 *
 * A stored annotation's id is a ULID (D4). These are `<planId>:<what>`, which is
 * not one — deliberately, because it makes an accidental save fail validation
 * loudly rather than quietly inserting a duplicate of a number the plan owns.
 * They are stable across renders, which is what the renderer actually needs.
 */

import type { Annotation, Execution, Plan, Trade } from "@qufolio/model";

import {
  LEVEL_TOKENS,
  executionMarks,
  levelLabel,
  levelsForSymbol,
  planLevels,
} from "./plan-levels";

/**
 * How a level's line is drawn.
 *
 * The stop is dashed and the targets are dashed; the entry is solid. A trader
 * reads one of these as "where I am" and the others as "where I might go", and
 * the dash is what says which is which without reading the label.
 */
const LEVEL_DASH: Record<string, number[] | undefined> = {
  entry: undefined,
  stop: [6, 4],
  target: [2, 4],
};

/**
 * The id a level draws under, and the only place the format is written.
 *
 * Parsed back by {@link parseLevelId} when a drag ends, so the two live
 * together — a format minted in one file and decoded in another is a format
 * that gets a separator added to it.
 */
export function levelId(planId: string, kind: string, targetId?: string): string {
  return `${planId}:${kind}:${targetId ?? ""}`;
}

/**
 * The zone and label ids, kept out of `parseLevelId`'s vocabulary on purpose.
 *
 * A drag on a shaded zone or a label must not amend a plan — only the line
 * does that — so these carry a prefix `parseLevelId` refuses.
 */
export const zoneId = (planId: string, kind: string, targetId?: string): string =>
  `zone|${levelId(planId, kind, targetId)}`;

export const labelId = (planId: string, kind: string, targetId?: string): string =>
  `label|${levelId(planId, kind, targetId)}`;

/**
 * One shaded band, entry to a level.
 *
 * A `box` with an `x1` and no `x2` is a ray, the same convention the lines use:
 * the renderer runs it to the last loaded bar, so the zone keeps reaching the
 * right edge as bars arrive rather than stopping where the data happened to end.
 */
function zone(
  planId: string,
  kind: "stop" | "target",
  from: number,
  entryPrice: number,
  price: number,
  token: string,
): Annotation {
  return {
    id: zoneId(planId, kind),
    type: "box",
    x1: from,
    y1: entryPrice,
    y2: price,
    style: {
      // Red below the entry, green above — the two questions a plan answers,
      // and the only two colours it needs. Tokens, never hex (D13).
      fill: token,
      stroke: "transparent",
      thickness: 0,
      opacity: 0.13,
    },
  };
}

/**
 * A level id back into what it addresses, or `null` if it is not one.
 *
 * `null` rather than a guess: an execution mark and a user's own drawing reach
 * the same handler, and treating either as a level would amend a plan because
 * somebody moved a trendline.
 */
export function parseLevelId(
  id: string,
): { planId: string; kind: string; targetId?: string } | null {
  // A zone or a label, which are `zone|<levelId>` and `label|<levelId>`. They
  // still split into three on the colon, so the prefix has to be refused
  // explicitly — otherwise dragging a shaded zone would amend the plan.
  if (id.includes("|")) return null;
  const parts = id.split(":");
  if (parts.length !== 3) return null;
  const [planId, kind, targetId] = parts;
  if (!planId || (kind !== "stop" && kind !== "target" && kind !== "entry")) return null;
  return { planId, kind, targetId: targetId || undefined };
}

/**
 * When a plan's lines start, in the seconds an annotation's x is stored in.
 *
 * `validFrom` when the plan names one, else when it was written. Not the fill:
 * the levels existed before anything filled, and starting them at the entry
 * would hide the whole point of a plan — that it was decided in advance.
 */
function planStart(plan: Plan): number | undefined {
  const at = plan.validFrom ?? plan.createdAt;
  if (!at) return undefined;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

/**
 * Every line one chart draws for its symbol's plans.
 *
 * **A ray from the plan, not a line across the pane.** These were `hline`s,
 * which span everything — and with two plans open that is four or six full-width
 * horizontal lines with nothing saying which belongs to which, crossing history
 * from years before the idea existed.
 *
 * A ray starts where the plan does and runs to the right edge, so its left end
 * *is* the answer to "when did I decide this", and two plans stack legibly
 * because they start in different places.
 *
 * Filtered by symbol here, so a chart showing AAPL never
 * draws the TSLA plan's stop — a horizontal line at a price that means nothing
 * on this chart and no way to tell why it is there.
 */
export function planAnnotations(
  plans: readonly Plan[],
  symbol: string,
  /**
   * The chart's last bar, in unix seconds.
   *
   * Only the label needs it, and only to sit **mid-ray**: at the plan's own
   * start it is jammed against whatever the ray begins on, which for a plan
   * drawn at the right edge is the edge itself. Halfway along is where there is
   * room, and it is still unambiguously on the line it names.
   */
  lastBar?: number,
): Annotation[] {
  const out: Annotation[] = [];

  for (const plan of plans) {
    if (plan.symbol.toUpperCase() !== symbol.toUpperCase()) continue;
    const levels = planLevels(plan);
    if (levels.length === 0) continue;
    const from = planStart(plan);
    const entry = levels.find((l) => l.kind === "entry");

    // **Two zones, not one per level.** A box per target overlapped every box
    // below it, so three targets stacked three greens and the shade said more
    // about how many exits there were than about the trade.
    //
    // Risk is entry to stop; reward is entry to the **furthest** target, which
    // is the whole range the plan plays for. The targets in between are lines
    // across it, which is what they are — places to take something off, not
    // separate zones.
    if (entry && from !== undefined) {
      const stop = levels.find((l) => l.kind === "stop");
      const targets = levels.filter((l) => l.kind === "target");
      // Furthest in the direction of the trade, not the highest price: a
      // short's targets step downwards.
      const furthest = targets.reduce<typeof targets[number] | undefined>(
        (best, t) =>
          !best || Math.abs(t.price - entry.price) > Math.abs(best.price - entry.price) ? t : best,
        undefined,
      );

      if (stop) {
        out.push(zone(plan.id, "stop", from, entry.price, stop.price, "negative"));
      }
      if (furthest) {
        out.push(zone(plan.id, "target", from, entry.price, furthest.price, "positive"));
      }
    }

    for (const level of levels) {
      out.push({
        // Stable across renders and unique per level. The target id is in the
        // key because two targets are two lines and a plan can carry several.
        id: levelId(level.planId, level.kind, level.targetId),
        type: from === undefined ? "hline" : "line",
        x1: from,
        y1: level.price,
        style: {
          stroke: LEVEL_TOKENS[level.kind],
          thickness: level.kind === "entry" ? 2 : 1,
          dash: LEVEL_DASH[level.kind],
        },
      });

      // The label as its own annotation, because a ray carries none. `hline`
      // has `showLabel` and a `line` does not — which is why these levels were
      // unlabelled the moment they stopped spanning the pane.
      if (from !== undefined) {
        out.push({
          id: labelId(level.planId, level.kind, level.targetId),
          type: "text",
          // Halfway between where the plan starts and the newest bar — see
          // `lastBar`. Falls back to the start when the chart has not said,
          // which is the old behaviour rather than no label.
          x1: lastBar !== undefined && lastBar > from ? Math.round((from + lastBar) / 2) : from,
          y1: level.price,
          // "Stop 177.00 · -4.3% · -1.00R" — a stop is only meaningful beside
          // what it costs, and a target beside what it pays.
          text: levelLabel(level),
          // 14, not 11. These carry a price, a percentage and an R — three
          // numbers someone reads at a glance while deciding something — and at
          // 11px against candles they were a smudge.
          style: { fontColor: LEVEL_TOKENS[level.kind], fontSize: 14 },
        });
      }
    }
  }

  return out;
}

/**
 * The fills, as marks where they happened.
 *
 * `text` rather than a shape, because the glyph and the size the fill was are
 * both worth reading at a glance and a marker carrying neither is just a dot.
 * A buy sits under its bar and a sell over it — `executionMarks` picks the
 * glyph, this only places it.
 */
export function executionAnnotations(
  executions: readonly Execution[],
  trades: readonly Trade[],
  symbol: string,
): Annotation[] {
  return executionMarks(executions, trades, symbol).map((mark) => ({
    id: `${mark.executionId}:fill`,
    type: "text",
    // Unix seconds — the same space the contract stores an annotation's x in,
    // and the renderer converts to a bar index at draw time.
    x1: mark.time,
    y1: mark.price,
    text: `${mark.glyph} ${mark.label}`,
    style: { fontColor: mark.token, fontSize: 11 },
  }));
}

/**
 * Everything the journal contributes to one chart, in draw order.
 *
 * Levels first and fills on top: a fill is a fact and a level is an intention,
 * so where they coincide — which is exactly at the entry, the interesting case
 * — the fact should be the one you can read.
 */
export function journalAnnotations(
  plans: readonly Plan[],
  trades: readonly Trade[],
  executions: readonly Execution[],
  symbol: string,
  /** The chart's last bar, so a label can sit mid-ray. See `planAnnotations`. */
  lastBar?: number,
): { annotations: Annotation[]; editable: Set<string> } {
  const levels = levelsForSymbol(plans, symbol);
  return {
    annotations: [
      ...planAnnotations(plans, symbol, lastBar),
      ...executionAnnotations(executions, trades, symbol),
    ],
    // **Which lines may be dragged, decided here rather than by the renderer.**
    //
    // A derived annotation is normally not editable, because a drag would edit
    // something whose source is elsewhere and the next render would silently
    // undo it. A plan level is the exception that proves the rule: it *has* a
    // writeback — `amendPlan` — so the drag reaches the number the line is drawn
    // from, and the next render agrees because the plan changed.
    //
    // `planLevels` already decides per level: the stop and the targets always,
    // the entry only while the plan is unfrozen. A filled plan's entry is a
    // fact, the server refuses to change it, and offering a grip that snaps
    // back is worse than offering none.
    editable: new Set(
      levels.filter((l) => l.draggable).map((l) => levelId(l.planId, l.kind, l.targetId)),
    ),
  };
}
